import React, { useEffect, useState, useMemo } from "react";
import { Users, RotateCcw } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const tooltipStyle = {
  contentStyle: { background: "#0d0d0d", border: "1px solid #00ff9033", borderRadius: 8, fontSize: 11 },
  labelStyle: { color: "#00ff90" },
};
const COLORS = ["#00ff90","#60a5fa","#c084fc","#fbbf24","#f87171","#34d399","#fb923c"];

export default function AgentWorkloadPanel({ localTickets = [], assignees = [] }) {
  const [apiData,  setApiData]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [from,     setFrom]     = useState("");
  const [to,       setTo]       = useState("");
  const [reassignFrom, setReassignFrom] = useState("");
  const [reassignTo,   setReassignTo]   = useState("");
  const [reassigning,  setReassigning]  = useState(false);
  const [msg, setMsg] = useState(null);
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetch(`${API}/api/admin/tickets/agent-workload`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setApiData(Array.isArray(d) ? d : null))
      .catch(() => setApiData(null))
      .finally(() => setLoading(false));
  }, []);

  // Derive from local tickets if no API data
  const workload = useMemo(() => {
    if (apiData) return apiData;
    const m = {};
    localTickets.forEach(t => {
      const a = t.assignee || "Unassigned";
      if (!m[a]) m[a] = { agent: a, open: 0, total: 0, avg_hrs: null };
      m[a].total++;
      if (t.status !== "Closed") m[a].open++;
    });
    return Object.values(m);
  }, [apiData, localTickets]);

  const bulkReassign = async () => {
    if (!reassignFrom || !reassignTo || reassignFrom === reassignTo) return;
    setReassigning(true); setMsg(null);
    try {
      await fetch(`${API}/api/admin/tickets/bulk-reassign`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ from: reassignFrom, to: reassignTo }),
      });
      setMsg({ type: "success", text: `✓ All tickets from ${reassignFrom} reassigned to ${reassignTo}.` });
    } catch (e) { setMsg({ type: "error", text: `⚠ ${e.message}` }); }
    finally { setReassigning(false); }
  };

  const chartData = workload.map((w, i) => ({ label: w.agent, value: w.open || w.total || 0 }));
  const AGENTS = assignees.length > 0 ? assignees : [...new Set(localTickets.map(t => t.assignee).filter(Boolean))];

  return (
    <div className={card}>
      <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2">
        <Users size={13} className="text-[#00ff90]/60" />
        <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Agent Workload</span>
      </div>

      <div className="p-5 space-y-4">
        {loading ? <div className="h-24 rounded-lg bg-white/5 animate-pulse" /> : (
          <>
            {/* Chart */}
            {chartData.length > 0 && (
              <ResponsiveContainer width="100%" height={100}>
                <BarChart data={chartData} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                  <XAxis dataKey="label" tick={{ fontSize: 9, fill: "rgba(255,255,255,0.3)" }} />
                  <YAxis tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }} />
                  <Tooltip {...tooltipStyle} formatter={v => [v, "Open tickets"]} />
                  <Bar dataKey="value" radius={[4,4,0,0]}>
                    {chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} fillOpacity={0.8} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}

            {/* Table */}
            <div className="space-y-2">
              {workload.map((w, i) => (
                <div key={w.agent} className="flex items-center justify-between rounded-lg border border-white/5 bg-white/3 px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                    <span className="text-sm text-white/70">{w.agent}</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs">
                    <span className="text-white/50">{w.open ?? w.total ?? 0} open</span>
                    {w.total != null && w.total !== w.open && <span className="text-white/30">{w.total} total</span>}
                    {w.avg_hrs != null && <span className="text-[#00ff90]/50">{w.avg_hrs}h avg</span>}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Bulk reassign */}
        <div className="pt-2 border-t border-white/5 space-y-3">
          <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider">Bulk Reassign (e.g. agent on leave)</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-white/30 block mb-1">From Agent</label>
              <select className={`${inputCls} w-full`} value={reassignFrom} onChange={e => setReassignFrom(e.target.value)}>
                <option value="">Select…</option>
                {AGENTS.map(a => <option key={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-white/30 block mb-1">To Agent</label>
              <select className={`${inputCls} w-full`} value={reassignTo} onChange={e => setReassignTo(e.target.value)}>
                <option value="">Select…</option>
                {AGENTS.map(a => <option key={a}>{a}</option>)}
              </select>
            </div>
          </div>
          {msg && <p className={`text-xs ${msg.type === "error" ? "text-red-400" : "text-[#00ff90]"}`}>{msg.text}</p>}
          <button onClick={bulkReassign} disabled={reassigning || !reassignFrom || !reassignTo}
            className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] disabled:opacity-40 transition-colors">
            <RotateCcw size={12} /> {reassigning ? "Reassigning…" : "Bulk Reassign"}
          </button>
        </div>
      </div>
    </div>
  );
}